// Core framework imports
import { defineStore } from 'pinia'
import { reactive, computed, toRaw } from 'vue'

// Store imports
import { useAuthStore } from '@/stores/auth.store.js'
import { useTranslationStore } from '@/stores/translation.store.js'

// API service imports
import conversationService from '@/services/api/conversation.service.js'
import messageService from '@/services/api/message.service.js'
import userService from '@/services/api/user.service.js'
import syncService from '@/services/api/sync.service.js'

// Socket service imports
import conversationSocketService from '@/services/socket/socketCoordinator.service.js'
import conversationSocketManager from '@/services/socket/conversationSocketManager.js'
import { disconnectSocket } from '@/services/socket/websocket.service.js'

// Database utility imports
import { getAllUsers, putUsers } from '@/utils/db/users.js'
import {
    updateConversation,
    deleteConversation,
    getAllConversations,
    getConversation,
    addConversation,
    findConversationByParticipantId,
} from '@/utils/db/conversations.js'
import { markMessages } from '@/utils/db/messages.js'

// Helper utility imports
import {
    createTemporaryMessage,
    handleOptimisticSendFailure,
} from './helpers/optimisticUiUtils.js'
import {
    loadInitialMessages,
    loadOlderMessages,
} from './helpers/messageLoaderUtils.js'

export const useConversationStore = defineStore(
    'conversation',
    () => {
        const authStore = useAuthStore()
        const translationStore = useTranslationStore()

        // ======================================================
        // ====================   STATE  ========================
        // ======================================================

        const state = reactive({
            currentMessages: [],
            pendingOptimisticMessages: {}, //Map of tempId to message
            currentConversation: null,
            conversations: [], // Loaded from DB initially
            usersMap: reactive({}), // ADDED usersMap to store user profiles by userId
            currentReceiverWriting: false,
            isLoadingMessages: false, // Renamed from isLoadingMoreMessages for clarity
            isLoadingConversations: false,
            hasMoreMessagesToLoad: false,
            conversationFilters: {
                mode: 'all', // 'all', 'favorited', 'request'
            },
            scrollPositions: {}, // Map of conversationId -> scrollTop for preserving scroll
            copilotSuggestionsByConversation: {}, // Map of conversationId -> suggestions[]
            copilotAudioIdeasByConversation: {}, // Map of conversationId -> { bullets: string[] }[]
        })

        // ======================================================
        // ===================  GETTERS  ======================
        // ======================================================

        function conversationSortTimestamp(conversation) {
            if (!conversation) return 0

            const lastMessageDate = conversation.lastMessage?.createdAt
            const fallbackDate =
                conversation.updatedAt || conversation.createdAt
            const rawDate = lastMessageDate || fallbackDate

            const timestamp = rawDate ? new Date(rawDate).getTime() : 0
            return Number.isFinite(timestamp) ? timestamp : 0
        }

        const filteredConversations = computed(() => {
            const conversationsByFilter = state.conversations.filter(
                (conversation) => {
                    // Ensure conversation exists before filtering
                    if (!conversation || !conversation.participantDetails)
                        return false

                    // Find the current user's participant details
                    const currentUserParticipant =
                        conversation.participantDetails.find(
                            (participant) =>
                                participant.userId === authStore.user?.data?._id
                        )

                    // Ensure participant details exist before filtering
                    if (
                        !currentUserParticipant ||
                        !currentUserParticipant.status
                    )
                        return false

                    if (state.conversationFilters.mode === 'all') return true
                    if (state.conversationFilters.mode === 'favorited')
                        return currentUserParticipant.status === 'favorited'
                    if (state.conversationFilters.mode === 'request')
                        return currentUserParticipant.status === 'request'
                    return false // Default case
                }
            )

            return conversationsByFilter.sort(
                (a, b) =>
                    conversationSortTimestamp(b) - conversationSortTimestamp(a)
            )
        })

        const myParticipantDetails = computed(() => {
            if (!state.currentConversation) return {}
            return (
                state.currentConversation.participantDetails.find(
                    (participant) =>
                        participant.userId === authStore.user?.data?._id
                ) || {}
            )
        })

        const otherParticipantDetails = computed(() => {
            if (!state.currentConversation) return null
            return state.currentConversation.participantDetails.find(
                (participant) =>
                    participant.userId !== authStore.user?.data?._id
            )
        })

        const currentChatPartner = computed(() => {
            if (!state.currentConversation) return null
            const currentChatPartnerId =
                state.currentConversation.participants.find(
                    (p) => p !== authStore.user?.data?._id
                )
            return state.usersMap[currentChatPartnerId]
        })

        // ======================================================
        // =============== INITIALIZATION & SYNC ===============
        // ======================================================

        async function initializeStore() {
            if (!authStore.user) return // Already initialized or no user

            console.log('Initializing Conversation Store...')
            state.isLoadingConversations = true
            state.copilotSuggestionsByConversation = {}
            state.copilotAudioIdeasByConversation = {}

            // Preserve currentConversation if it's already set (e.g., from auto-upgrade flow)
            const preservedCurrentConversation = state.currentConversation
            const preservedCurrentMessages = state.currentMessages
                ? [...state.currentMessages]
                : []

            try {
                // 1. Check if initial backend sync is needed (based on db appConfig)
                const syncResult = await syncService.initialDataSync()

                // 2. Load conversations from IndexedDB into Pinia state
                const localConversations = await getAllConversations()
                state.conversations = localConversations
                console.log(
                    `Loaded ${localConversations.length} conversations from IndexedDB into pinia state.`
                )

                // Restore currentConversation if it was set before initialization
                if (preservedCurrentConversation) {
                    console.log(
                        'Restoring preserved currentConversation:',
                        preservedCurrentConversation._id
                    )
                    state.currentConversation = preservedCurrentConversation
                    state.currentMessages = preservedCurrentMessages
                }

                // ADDED: Load all users from IndexedDB into usersMap
                const allUsersFromDb = await getAllUsers()
                if (allUsersFromDb && allUsersFromDb.length > 0) {
                    allUsersFromDb.forEach((user) => {
                        state.usersMap[user._id] = user
                    })
                    console.log(
                        `Loaded ${allUsersFromDb.length} users into usersMap from IndexedDB.`
                    )
                }

                // Process conversations/messages/users returned from a background sync
                // First, merge any newly synced conversations so they are present before joining rooms
                if (
                    syncResult &&
                    syncResult.syncedConversations &&
                    syncResult.syncedConversations.length > 0
                ) {
                    console.log(
                        `Processing ${syncResult.syncedConversations.length} conversations from background sync...`
                    )
                    const existingIds = new Set(
                        state.conversations.map((c) => c._id)
                    )
                    const merged = [...state.conversations]
                    syncResult.syncedConversations.forEach((convo) => {
                        if (!existingIds.has(convo._id)) {
                            merged.push(convo)
                        } else {
                            const idx = merged.findIndex(
                                (c) => c._id === convo._id
                            )
                            if (idx > -1)
                                merged[idx] = { ...merged[idx], ...convo }
                        }
                    })
                    state.conversations = merged

                    // Update currentConversation if it was one of the synced ones
                    if (state.currentConversation) {
                        const syncedCurrent =
                            syncResult.syncedConversations.find(
                                (c) => c._id === state.currentConversation._id
                            )
                        if (syncedCurrent) {
                            console.log(
                                'Updating currentConversation from background sync'
                            )
                            state.currentConversation = {
                                ...state.currentConversation,
                                ...syncedCurrent,
                            }
                        }
                    }

                    console.log(
                        'Finished processing conversations from background sync.'
                    )
                }

                // Process messages returned from a background sync
                if (
                    syncResult &&
                    syncResult.syncedMessages &&
                    syncResult.syncedMessages.length > 0
                ) {
                    console.log(
                        `Processing ${syncResult.syncedMessages.length} messages from background sync...`
                    )
                    const { syncedMessages } = syncResult

                    syncedMessages.forEach((syncedMessage) => {
                        if (syncedMessage?.conversationId) {
                            delete state.copilotSuggestionsByConversation[
                                syncedMessage.conversationId
                            ]
                            delete state.copilotAudioIdeasByConversation[
                                syncedMessage.conversationId
                            ]
                        }
                        // Update currentMessages if the synced message belongs to the current conversation
                        if (
                            state.currentConversation &&
                            state.currentConversation._id ===
                            syncedMessage.conversationId
                        ) {
                            const existingMsgIndex =
                                state.currentMessages.findIndex(
                                    (m) => m._id === syncedMessage._id
                                )
                            if (existingMsgIndex > -1) {
                                state.currentMessages.splice(
                                    existingMsgIndex,
                                    1,
                                    syncedMessage
                                )
                            } else {
                                // Add here, if problems with order occur consider sorting here
                                state.currentMessages.push(syncedMessage)
                            }
                        }

                        // Update the lastMessage in the corresponding conversation in state.conversations
                        const convoIndex = state.conversations.findIndex(
                            (c) => c._id === syncedMessage.conversationId
                        )
                        if (convoIndex > -1) {
                            const conversation = state.conversations[convoIndex]
                            if (
                                !conversation.lastMessage ||
                                new Date(syncedMessage.createdAt) >
                                new Date(conversation.lastMessage.createdAt)
                            ) {
                                state.conversations[convoIndex].lastMessage =
                                    syncedMessage
                            }
                        }
                    })
                    console.log(
                        'Finished processing messages from background sync.'
                    )
                }

                // Process users returned from a background sync
                if (
                    syncResult &&
                    syncResult.syncedUsers &&
                    syncResult.syncedUsers.length > 0
                ) {
                    console.log(
                        `Processing ${syncResult.syncedUsers.length} users from background sync...`
                    )
                    const { syncedUsers } = syncResult

                    syncedUsers.forEach((syncedUser) => {
                        state.usersMap[syncedUser._id] = syncedUser
                    })
                    console.log(
                        'Finished processing users from background sync.'
                    )
                }

                // 3. Setup WebSocket Listeners via the new manager
                // The old setupSocketListenersInternal() is replaced by this direct call.
                if (authStore.user.data) {
                    // Ensure authStore is ready
                    conversationSocketManager.initializeConversationSocketHandlers(
                        this,
                        authStore
                    )
                } else {
                    console.warn(
                        'ConversationStore: User data not available, cannot initialize socket handlers.'
                    )
                }

                // 5. Join all existing conversation rooms
                if (state.conversations.length > 0 && authStore.user.data) {
                    await conversationSocketService.joinAllExistingConversations(
                        state.conversations,
                        authStore.user.data
                    )
                } else {
                    console.log(
                        'No conversations to join or user data missing.'
                    )
                }

                // 6. Setup handlers for reconnection events
                if (authStore.user.data) {
                    // Pass getter functions to ensure the handlers use fresh data upon execution
                    const getConversations = () => state.conversations
                    const getCurrentConversation = () =>
                        state.currentConversation
                    const syncCallback = (convoId) =>
                        setConversation(convoId, { force: true })

                    conversationSocketService.setupReconnectionHandler(
                        getConversations,
                        authStore.user.data
                    )
                    conversationSocketService.setupMessageReconnectionHandler(
                        getCurrentConversation,
                        syncCallback
                    )
                    console.log(
                        'Conversation store reconnection handlers setup.'
                    )
                }

                console.log('Conversation Store Initialized.')
            } catch (error) {
                console.error('Failed to initialize conversation store:', error)
                // Handle initialization error appropriately (e.g., show error message)
            } finally {
                state.isLoadingConversations = false
            }

            // ADD THIS:
            // After initial setup and potential rehydration from session storage,
            // if there's a currentConversation, ensure its messages are loaded and UI is set.
            // Using a microtask to ensure Pinia's own hydration is fully complete and
            // other initializations might have run.
            Promise.resolve().then(() => {
                if (
                    state.currentConversation &&
                    state.currentConversation._id
                ) {
                    console.log(
                        `ConversationStore: Re-setting current conversation ${state.currentConversation._id} after store initialization/rehydration.`
                    )
                    // Call setConversation with force option to ensure it runs even if ID is the same.
                    // This will trigger message loading and the subsequent scrollToBottom.
                    setConversation(state.currentConversation._id, {
                        force: true,
                    })
                }
            })
        }

        // ======================================================
        // ============ TRANSLATION HELPER ACTIONS =============
        // ======================================================

        /**
         * Fetches missing translations for a given list of messages and updates state/DB.
         * @param {Array<IMessage>} messagesToCheck - The messages to check for missing translations.
         */
        async function fetchAndMergeTranslations(messagesToCheck) {
            if (
                !authStore.user?.data?.translateMessages ||
                !messagesToCheck ||
                messagesToCheck.length === 0
            ) {
                return messagesToCheck
            }

            const preferredLanguage =
                translationStore.getSelectedLanguage() ||
                authStore.user?.data?.preferredLanguage

            if (!preferredLanguage) {
                console.warn(
                    'Skipping translation fetch: No preferred language set.'
                )
                return messagesToCheck
            }

            const messagesNeedingTranslation = messagesToCheck.filter(
                (msg) =>
                    msg.sender !== authStore.userId &&
                    ((msg.content &&
                        (!msg.translations ||
                            !msg.translations[preferredLanguage])) ||
                        (msg.contentType === 'audio' &&
                            msg.transcript &&
                            (!msg.translations ||
                                !msg.translations[
                                `transcript_${preferredLanguage}`
                                ])))
            )

            if (messagesNeedingTranslation.length === 0) {
                return messagesToCheck
            }

            const idsToTranslate = messagesNeedingTranslation.map(
                (msg) => msg._id
            )

            try {
                const { messages: updatedMessages } =
                    await messageService.fetchTranslationsForMessages(
                        idsToTranslate,
                        preferredLanguage
                    )

                if (updatedMessages && updatedMessages.length > 0) {
                    const updatedMessagesMap = new Map(
                        updatedMessages.map((msg) => [
                            msg._id,
                            msg.translations,
                        ])
                    )

                    return messagesToCheck.map((existingMsg) => {
                        if (updatedMessagesMap.has(existingMsg._id)) {
                            return {
                                ...existingMsg,
                                translations: {
                                    ...(existingMsg.translations || {}),
                                    ...updatedMessagesMap.get(existingMsg._id),
                                },
                            }
                        }
                        return existingMsg
                    })
                }
                return messagesToCheck
            } catch (error) {
                console.error(
                    'Failed to fetch or process message translations:',
                    error
                )
                return messagesToCheck
            }
        }

        /**
         * Checks all current messages and fetches any missing translations if enabled.
         * Called when translation is toggled ON.
         */
        async function ensureCurrentMessagesTranslated() {
            if (state.currentMessages.length > 0) {
                state.currentMessages = await fetchAndMergeTranslations(
                    state.currentMessages
                )
            }
        }

        // ======================================================
        // =================== STORE ACTIONS ====================
        // ======================================================

        async function setConversation(conversationId, options = {}) {
            if (!conversationId) {
                console.error(
                    'setConversation called with null/undefined conversationId'
                )
                return
            }
            // 1. Check if already current (unless options.force)
            if (
                state.currentConversation?._id === conversationId &&
                !options.force
            ) {
                console.log(
                    `Conversation ${conversationId} is already current.`
                )
                return
            }

            console.log(`Setting current conversation to: ${conversationId}`)
            state.currentMessages = []
            state.hasMoreMessagesToLoad = false

            try {
                // 2. Try fetching conversation details from DB
                let conversation = await getConversation(conversationId)

                // 3. If not found locally, fetch from API
                if (!conversation) {
                    state.isLoadingMessages = true
                    console.log(
                        `Conversation ${conversationId} not found locally, fetching from API...`
                    )
                    conversation =
                        await conversationService.getConversation(
                            conversationId
                        )
                    if (conversation) {
                        // Add to DB and potentially state.conversations (async)
                        addConversation(conversation).catch((err) =>
                            console.error(
                                'DB Error adding fetched conversation:',
                                err
                            )
                        )
                        if (
                            !state.conversations.some(
                                (c) => c._id === conversationId
                            )
                        ) {
                            state.conversations.push(conversation)
                        }
                    } else {
                        throw new Error(
                            `Conversation ${conversationId} not found via API.`
                        )
                    }
                }

                // 4. Set state.currentConversation
                state.currentConversation = conversation

                // Set loading state before message loading starts
                state.isLoadingMessages = true

                // --- Message Loading --- Use Helper ---
                const {
                    messages: initialCombinedMessages,
                    hasMore: initialHasMore,
                } = await loadInitialMessages(conversationId)

                state.currentMessages = initialCombinedMessages
                state.hasMoreMessagesToLoad = initialHasMore

                // --- Check and Fetch Translations if Enabled ---
                if (authStore.user?.data?.translateMessages) {
                    state.currentMessages = await fetchAndMergeTranslations(
                        state.currentMessages
                    )
                }

                // --- Mark messages as read (async) ---
                markCurrentConversationRead() // Don't await
            } catch (error) {
                console.error(
                    `Failed to set conversation ${conversationId}:`,
                    error
                )
                state.currentConversation = null
            } finally {
                state.isLoadingMessages = false
            }
        }

        async function markCurrentConversationRead() {
            if (!state.currentConversation || !authStore.user?.data) {
                console.log(
                    'Skipping mark as read: No current conversation or user.'
                )
                return
            }

            const conversationId = state.currentConversation._id

            // Filter messages that need marking as read (sent by others, not yet read by current user)
            const messagesToMark = state.currentMessages.filter(
                (msg) =>
                    msg.sender !== authStore.userId && // Sent by someone else
                    !msg.readBy?.includes(authStore.userId) // Not already marked as read by me
            )

            if (messagesToMark.length === 0) {
                // console.log("No new messages to mark as read.");
                return
            }

            const messageIdsToMark = messagesToMark.map((msg) => msg._id)
            console.log(
                `Marking ${messageIdsToMark.length} messages as read for user ${authStore.userId} in convo ${conversationId}...`
            )

            try {
                // 1. Call API (fire-and-forget is okay here, but we can await)
                await messageService.markMessagesAsRead(messageIdsToMark)
                console.log('API call to mark messages as read successful.')

                // 2. Update IndexedDB (async)
                markMessages(messageIdsToMark, authStore.userId, {
                    read: true,
                }).catch((err) =>
                    console.error('DB Error marking messages as read:', err)
                )

                // 3. Update Pinia state immediately (already done optimistically by load/socket)
                // We just need to ensure the state reflects reality after API call confirms.
                // Note: The `handleMessageStatusUpdate` via socket might also update this,
                // but doing it here ensures consistency even if socket events are delayed.
                state.currentMessages = state.currentMessages.map((message) => {
                    if (
                        messageIdsToMark.includes(message._id) &&
                        !message.readBy?.includes(authStore.userId)
                    ) {
                        return {
                            ...message,
                            readBy: [
                                ...(message.readBy || []),
                                authStore.userId,
                            ],
                        }
                    }
                    return message
                })
                console.log('Updated Pinia state for read messages.')
            } catch (error) {
                console.error('Failed to mark messages as read:', error)
                // Decide on error handling (e.g., retry logic?)
            }
        }

        async function loadMoreMessages() {
            // 1. Check conditions
            if (
                !state.currentConversation ||
                !state.hasMoreMessagesToLoad ||
                state.isLoadingMessages
            ) {
                console.log('Conditions not met for loading more messages:', {
                    hasConv: !!state.currentConversation,
                    hasMore: state.hasMoreMessagesToLoad,
                    loading: state.isLoadingMessages,
                })
                return
            }

            console.log(
                `Loading more messages for ${state.currentConversation._id}...`
            )
            state.isLoadingMessages = true

            try {
                const oldestMessage = state.currentMessages[0]
                const beforeTimestamp = oldestMessage?.createdAt

                if (!beforeTimestamp) {
                    console.warn(
                        'Cannot load more messages: No oldest message found.'
                    )
                    state.hasMoreMessagesToLoad = false // Should not happen if hasMore is true
                    return
                }

                console.log(`Fetching messages before: ${beforeTimestamp}`)

                // --- Message Loading --- Use Helper ---
                const {
                    newMessages: uniqueNewOlderMessages,
                    hasMore: newHasMore,
                } = await loadOlderMessages(
                    state.currentConversation._id,
                    beforeTimestamp,
                    100, // limit
                    state.currentMessages
                )

                // --- Check and Fetch Translations for the NEW Batch if Enabled ---
                let messagesToPrepend = uniqueNewOlderMessages
                if (
                    authStore.user?.data?.translateMessages &&
                    uniqueNewOlderMessages.length > 0
                ) {
                    messagesToPrepend = await fetchAndMergeTranslations(
                        uniqueNewOlderMessages
                    )
                }

                if (messagesToPrepend.length > 0) {
                    // Prepend the (potentially translation-enriched) batch
                    state.currentMessages.unshift(...messagesToPrepend)
                    console.log(
                        `Store: Prepended ${messagesToPrepend.length} new older messages.`
                    )
                }

                state.hasMoreMessagesToLoad = newHasMore
                console.log(
                    `Store: Load more complete. Total: ${state.currentMessages.length}. HasMore: ${state.hasMoreMessagesToLoad}`
                )
            } catch (error) {
                console.error('Failed to load more messages:', error)
            } finally {
                state.isLoadingMessages = false
            }
        }

        async function favoriteConversation(conversationId) {
            console.log(
                `Attempting to favorite conversation ${conversationId}...`
            )
            try {
                // 1. Call API Service
                const result =
                    await conversationService.favoriteConversation(
                        conversationId
                    )

                if (result.success) {
                    console.log(
                        `API Success for favoriting ${conversationId}. Re-fetching full conversation for consistency.`
                    )
                    // Re-fetch the full conversation object to ensure all details are up-to-date
                    const updatedFullConversation =
                        await conversationService.getConversation(
                            conversationId
                        )

                    if (
                        updatedFullConversation &&
                        updatedFullConversation._id
                    ) {
                        console.log(
                            `Successfully re-fetched conversation ${conversationId}.`
                        )

                        // 2. Update Pinia State
                        const index = state.conversations.findIndex(
                            (c) => c._id === conversationId
                        )
                        if (index !== -1) {
                            state.conversations.splice(
                                index,
                                1,
                                updatedFullConversation
                            )
                            console.log(
                                'Updated conversation status in Pinia state (conversations list).'
                            )
                        }
                        // Update current conversation if it's the one being favorited
                        if (state.currentConversation?._id === conversationId) {
                            state.currentConversation = updatedFullConversation
                            console.log(
                                'Updated currentConversation in Pinia state.'
                            )
                        }

                        // 3. Update IndexedDB
                        await updateConversation(updatedFullConversation)
                        console.log('Updated conversation in IndexedDB.')
                    } else {
                        console.error(
                            `Failed to re-fetch full conversation details for ${conversationId} after favoriting, or it lacked an _id.`
                        )
                        // Optionally, throw an error or handle this case more specifically
                    }
                } else {
                    console.error(
                        `API failed to favorite conversation ${conversationId}. Response:`,
                        result
                    )
                    throw new Error(
                        result.message ||
                        `API failed to favorite conversation ${conversationId}.`
                    )
                }
            } catch (error) {
                console.error(
                    `Error during favoriteConversation action for ${conversationId}:`,
                    error
                )
                throw error // Re-throw to allow component to handle it
            }
        }

        async function unfavoriteConversation(conversationId) {
            console.log(
                `Attempting to unfavorite conversation ${conversationId}...`
            )
            try {
                // 1. Call API Service
                const result =
                    await conversationService.unfavoriteConversation(
                        conversationId
                    )

                if (result.success) {
                    console.log(
                        `API Success for unfavoriting ${conversationId}. Re-fetching full conversation for consistency.`
                    )
                    const updatedFullConversation =
                        await conversationService.getConversation(
                            conversationId
                        )

                    if (
                        updatedFullConversation &&
                        updatedFullConversation._id
                    ) {
                        console.log(
                            `Successfully re-fetched conversation ${conversationId}.`
                        )
                        // 2. Update Pinia State
                        const index = state.conversations.findIndex(
                            (c) => c._id === conversationId
                        )
                        if (index !== -1) {
                            state.conversations.splice(
                                index,
                                1,
                                updatedFullConversation
                            )
                        }
                        if (state.currentConversation?._id === conversationId) {
                            state.currentConversation = updatedFullConversation
                        }

                        // 3. Update IndexedDB
                        await updateConversation(updatedFullConversation)
                        console.log(
                            `Updated conversation ${conversationId} in Pinia and DB after unfavoriting.`
                        )
                    } else {
                        console.error(
                            `Failed to re-fetch full conversation details for ${conversationId} after unfavoriting, or it lacked an _id.`
                        )
                    }
                } else {
                    console.error(
                        `API failed to unfavorite conversation ${conversationId}. Response:`,
                        result
                    )
                    throw new Error(
                        result.message ||
                        `API failed to unfavorite conversation ${conversationId}.`
                    )
                }
            } catch (error) {
                console.error(
                    `Error during unfavoriteConversation action for ${conversationId}:`,
                    error
                )
                throw error
            }
        }

        async function leaveConversation(conversationId) {
            console.log(`Attempting to leave conversation ${conversationId}...`)
            try {
                // 1. Call API Service
                const result =
                    await conversationService.leaveConversation(conversationId)

                if (result.success) {
                    console.log(
                        `API Success: Left conversation ${conversationId}.`
                    )

                    // 2. Update Pinia State
                    const index = state.conversations.findIndex(
                        (c) => c._id === conversationId
                    )
                    if (index !== -1) {
                        state.conversations.splice(index, 1)
                        console.log('Removed conversation from Pinia state.')
                    }
                    if (state.currentConversation?._id === conversationId) {
                        state.currentConversation = null // Clear if leaving the current one
                        state.currentMessages = []
                        state.hasMoreMessagesToLoad = false
                    }

                    // 3. Update IndexedDB (async, fire-and-forget)
                    deleteConversation(conversationId).catch((err) =>
                        console.error(
                            'DB Error deleting left conversation:',
                            err
                        )
                    )
                } else {
                    console.error(
                        `API failed to leave conversation ${conversationId}.`
                    )
                }
            } catch (error) {
                console.error(
                    `Error during leaveConversation action for ${conversationId}:`,
                    error
                )
            }
        }

        // Approve conversation (similar pattern)
        async function approveConversation(conversationId) {
            console.log(
                `Attempting to approve conversation ${conversationId}...`
            )
            try {
                const result =
                    await conversationService.approveConversation(
                        conversationId
                    )
                if (result.success) {
                    console.log(
                        `API Success for approving ${conversationId}. Re-fetching full conversation for consistency.`
                    )
                    const updatedFullConversation =
                        await conversationService.getConversation(
                            conversationId
                        )

                    if (
                        updatedFullConversation &&
                        updatedFullConversation._id
                    ) {
                        console.log(
                            `Successfully re-fetched conversation ${conversationId}.`
                        )
                        // Update Pinia
                        const index = state.conversations.findIndex(
                            (c) => c._id === conversationId
                        )
                        if (index !== -1) {
                            state.conversations.splice(
                                index,
                                1,
                                updatedFullConversation
                            )
                        }
                        if (state.currentConversation?._id === conversationId) {
                            state.currentConversation = updatedFullConversation
                        }
                        // Update DB
                        await updateConversation(updatedFullConversation)
                        console.log(
                            `Updated conversation ${conversationId} in Pinia and DB after approving.`
                        )
                    } else {
                        console.error(
                            `Failed to re-fetch full conversation details for ${conversationId} after approving, or it lacked an _id.`
                        )
                    }
                } else {
                    console.error(
                        `API failed to approve conversation ${conversationId}. Response:`,
                        result
                    )
                    throw new Error(
                        result.message ||
                        `API failed to approve conversation ${conversationId}.`
                    )
                }
            } catch (error) {
                console.error(
                    `Error during approveConversation action for ${conversationId}:`,
                    error
                )
                throw error
            }
        }

        // Send message action with optimistic UI - Now split into multiple steps
        // Step 1: Add the optimistic message shell (e.g., for image/video preview)
        async function addOptimisticMessage(messagePayload) {
            if (!state.currentConversation) {
                console.error(
                    'Cannot add message: No current conversation selected.'
                )
                return null
            }

            if (!authStore.user?.data) {
                console.error('Cannot add message: No user data.')
                return null
            }

            // 1. Create temporary message using helper
            const tempMessage = createTemporaryMessage(
                messagePayload,
                state.currentConversation,
                authStore.user?.data
            )

            if (!tempMessage) {
                console.error('Failed to create temporary message object.')
                return null
            }

            console.log('Optimistically adding message shell:', tempMessage)

            // 2. Add message with temp ID to Pinia state
            state.currentMessages.push(tempMessage)
            state.pendingOptimisticMessages[tempMessage.tempId] = tempMessage

            // 3. Return the tempId so the component can use it for finalize/fail steps
            return tempMessage.tempId
        }

        // Step 2: Finalize the optimistic message (send to API after upload etc.)
        async function finalizeOptimisticMessage(payload) {
            const {
                tempId,
                contentType,
                content,
                mediaObjectId,
                price,
                premium,
                orderId,
            } = payload

            if (!tempId || !contentType) {
                console.error(
                    'Invalid payload for finalizeOptimisticMessage',
                    payload
                )
                // Attempt to mark as failed if tempId is known
                if (tempId) {
                    await markOptimisticMessageFailed(tempId)
                }
                throw new Error('Invalid payload for finalization.')
            }

            // Find the original temporary message from the pending map, not currentMessages
            const tempMessage = state.pendingOptimisticMessages[tempId]
            if (!tempMessage) {
                console.error(
                    `Temporary message ${tempId} not found in pending map for finalization.`
                )
                // If the message is not in the pending map, it might have already been processed or failed.
                // We should not proceed and should not mark it as failed again.
                throw new Error(
                    `Temporary message ${tempId} not found in pending map.`
                )
            }

            // Prepare API payload conditionally
            const apiPayload = {
                tempId: tempId,
                conversationId: tempMessage.conversationId,
                sender: tempMessage.sender,
                receiver: tempMessage.receiver,
                content,
                price: price,
                contentType: contentType,
                // Include mediaObjectId only for media types
                ...((contentType === 'image' ||
                    contentType === 'audio' ||
                    contentType === 'video') && {
                    mediaObjectId: mediaObjectId || content,
                }),
                // Include duration for audio/video
                ...(contentType === 'audio' && {
                    duration: tempMessage.duration || 0,
                }),
                ...(contentType === 'video' && {
                    duration: tempMessage.duration || 0,
                }),
                premium,
                orderId,
                // DO NOT send localFileUrl or isOptimistic to backend
            }

            const confirmedMessages = state.currentMessages.filter(
                (msg) => !msg.isOptimistic && msg._id
            )
            const hasAnyMessages = confirmedMessages.length > 0
            const hasSenderSentBefore = confirmedMessages.some(
                (msg) => msg.sender === authStore.userId
            )
            const isFirstMessageInConversation = !hasAnyMessages
            const isFirstReplyInConversation =
                hasAnyMessages && !hasSenderSentBefore

            const trackingContext = {
                isFirstMessage: isFirstMessageInConversation,
                isFirstReply: isFirstReplyInConversation,
            }

            console.log(
                `Finalizing message ${tempId}, sending API payload:`,
                apiPayload
            )

            try {
                const sentMessage = await messageService.sendMessage(
                    apiPayload,
                    trackingContext
                )
                console.log(
                    `API Success: Message ${tempId} finalized, backend returned:`,
                    sentMessage
                )

                // If backend returned updated credits, persist them on the conversation for UI (banner/disable)
                const credits = sentMessage?.messageCredits
                if (credits && state.currentConversation?._id) {
                    const convId = state.currentConversation._id
                    // Update currentConversation
                    state.currentConversation = {
                        ...state.currentConversation,
                        paidMessaging: {
                            ...(state.currentConversation.paidMessaging || {}),
                            remaining: credits,
                        },
                    }

                    // Update conversations list entry
                    const idx = state.conversations.findIndex(
                        (c) => c._id === convId
                    )
                    if (idx > -1) {
                        state.conversations[idx] = {
                            ...state.conversations[idx],
                            paidMessaging: {
                                ...(state.conversations[idx].paidMessaging ||
                                    {}),
                                remaining: credits,
                            },
                        }
                    }

                    // Persist to IndexedDB (use toRaw to convert Proxy to plain object)
                    updateConversation(toRaw(state.currentConversation)).catch(
                        (err) =>
                            console.error(
                                'DB Error updating conversation credits:',
                                err
                            )
                    )
                }
            } catch (error) {
                console.error(`API Error finalizing message ${tempId}:`, error)

                // If backend rejected due to insufficient credits, update local remaining from payload
                const isCreditsError =
                    error?.response?.status === 402 &&
                    error?.response?.data?.code ===
                    'insufficient_message_credits'
                const remaining = error?.response?.data?.data?.remaining

                if (
                    isCreditsError &&
                    remaining &&
                    state.currentConversation?._id
                ) {
                    const convId = state.currentConversation._id
                    state.currentConversation = {
                        ...state.currentConversation,
                        paidMessaging: {
                            ...(state.currentConversation.paidMessaging || {}),
                            remaining,
                        },
                    }
                    const idx = state.conversations.findIndex(
                        (c) => c._id === convId
                    )
                    if (idx > -1) {
                        state.conversations[idx] = {
                            ...state.conversations[idx],
                            paidMessaging: {
                                ...(state.conversations[idx].paidMessaging ||
                                    {}),
                                remaining,
                            },
                        }
                    }
                    updateConversation(toRaw(state.currentConversation)).catch(
                        (err) =>
                            console.error(
                                'DB Error updating conversation credits from 402:',
                                err
                            )
                    )
                }

                // Handle error using helper
                await markOptimisticMessageFailed(tempId)
                // Re-throw the error so the component handler knows it failed
                throw error
            }
        }

        // Step 3: Mark an optimistic message as failed
        async function markOptimisticMessageFailed(tempId) {
            if (!tempId) {
                console.warn(
                    'markOptimisticMessageFailed called without tempId'
                )
                return
            }
            console.log(`Marking optimistic message ${tempId} as failed.`)

            // Remove from pending list
            if (state.pendingOptimisticMessages[tempId]) {
                delete state.pendingOptimisticMessages[tempId]
                console.log(
                    `Removed optimistic message ${tempId} from pending list.`
                )
            }

            // Use the utility function to update UI in currentMessages and revoke blob
            handleOptimisticSendFailure(state.currentMessages, tempId)
        }

        async function updateOrderInMessage(order) {
            // check if there is a message that contains the order._id and update it
            const message = state.currentMessages.find(
                (m) => m.order?._id === order._id
            )
            if (message) {
                message.order = order
            }
        }

        async function updateMediaObjectRefsInMessages(mediaObjects) {
            // Create a map of media objects by their ID for quick lookup
            const mediaObjectsMap = new Map(mediaObjects.map((m) => [m._id, m]))

            // Only update messages that actually contain one of the media objects
            state.currentMessages.forEach((message) => {
                const mediaObjectId = message.mediaObjectRef?._id
                if (mediaObjectId && mediaObjectsMap.has(mediaObjectId)) {
                    // Replace the media object reference with the updated one
                    message.mediaObjectRef = mediaObjectsMap.get(mediaObjectId)
                }
            })
        }

        async function updateMessageTranscripts(transcripts) {
            if (!transcripts || transcripts.length === 0) return

            const transcriptsMap = new Map(
                transcripts.map((t) => [t.messageId, t])
            )

            const messagesUpdated = []

            state.currentMessages.forEach((message) => {
                const updateData = transcriptsMap.get(message._id)
                if (updateData) {
                    message.transcript = updateData.transcript
                    message.transcriptLanguage = updateData.transcriptLanguage
                    message.transcriptionStatus = updateData.transcriptionStatus
                    if (
                        updateData.translations &&
                        Object.keys(updateData.translations).length > 0
                    ) {
                        if (!message.translations) message.translations = {}
                        Object.assign(
                            message.translations,
                            updateData.translations
                        )
                    }
                    messagesUpdated.push(message)
                }
            })

            // If we have updated messages, ensure they have the correct translations
            // This handles cases where the translation wasn't generated yet or the user's
            // preferred language differs from what's stored
            if (
                messagesUpdated.length > 0 &&
                authStore.user?.data?.translateMessages
            ) {
                const updatedWithTranslations =
                    await fetchAndMergeTranslations(messagesUpdated)

                // Merge any newly fetched translations back into state.currentMessages
                // Note: fetchAndMergeTranslations returns a new array of objects with translations merged
                const translationsMap = new Map(
                    updatedWithTranslations.map((m) => [m._id, m.translations])
                )

                state.currentMessages.forEach((message) => {
                    const newTranslations = translationsMap.get(message._id)
                    if (newTranslations) {
                        if (!message.translations) message.translations = {}
                        Object.assign(message.translations, newTranslations)
                    }
                })
            }
        }

        // Filters - These modify state directly, which is fine
        function setMode(mode) {
            // mode is 'all', 'request', 'favorited'
            if (['all', 'request', 'favorited'].includes(mode)) {
                state.conversationFilters.mode = mode
                state.currentConversation = null // Reset selection when changing filter
                console.log('Conversation filter mode set to:', mode)
            } else {
                console.warn('Invalid mode set for conversation filter:', mode)
            }
        }

        // ======================================================
        // =================   CLEANING =========================
        // ======================================================

        function logoutClean() {
            console.log('Cleaning up conversation store for logout...')
            state.currentConversation = null
            state.conversations = []
            state.currentMessages = []
            state.usersMap = {}
            state.hasMoreMessagesToLoad = false
            state.isLoadingMessages = false
            state.isLoadingConversations = false
            state.conversationFilters.mode = 'all'
            state.currentReceiverWriting = false

            // 1. Clean up socket listeners managed by the conversation service
            conversationSocketService.cleanupSockets()

            // 2. Disconnect the actual socket connection
            disconnectSocket()

            console.log('Conversation store cleaned and socket disconnected.')
        }

        async function createConversation(receiverUsername, chatPreferences = null) {
            console.log(
                `Attempting to create conversation with ${receiverUsername}...`
            )
            // Optionally add a loading state if needed for UI feedback
            // state.isCreatingConversation = true;
            try {
                // 1. Call API Service
                const result =
                    await conversationService.createConversation(
                        receiverUsername,
                        chatPreferences
                    )

                if (result && result.conversation) {
                    console.log(
                        'API Success: Conversation created/retrieved:',
                        result.conversation._id
                    )

                    // 2. Fetch the full conversation details
                    const newConversation =
                        await conversationService.getConversation(
                            result.conversation._id
                        )

                    if (newConversation) {
                        // Update Pinia State (add if not present)
                        if (
                            !state.conversations.some(
                                (c) => c._id === newConversation._id
                            )
                        ) {
                            state.conversations.push(newConversation)
                            console.log(
                                'Added new conversation to Pinia lists.'
                            )
                        }

                        // Update IndexedDB (async, fire-and-forget)
                        await updateConversation(newConversation) // Save/Update in IndexedDB <-- CORRECT

                        // 4. Set as current conversation
                        await setConversation(newConversation._id)

                        // 6. Join the socket room
                        if (authStore.user.data) {
                            conversationSocketService.joinNewConversation(
                                newConversation._id,
                                authStore.user.data
                            )
                        }

                        // 7. Get the receiver user details and save in IndexedDB/Pinia
                        const receiverId =
                            newConversation.participantDetails.find(
                                (p) => p.userId !== authStore.userId
                            ).userId

                        // Sync users related to conversations and pick the one we need by id
                        const usersDataResponse =
                            await userService.getUserData()
                        if (
                            usersDataResponse?.data &&
                            Array.isArray(usersDataResponse.data)
                        ) {
                            const matchedUser = usersDataResponse.data.find(
                                (u) => u._id === receiverId
                            )
                            if (matchedUser) {
                                state.usersMap[receiverId] = matchedUser
                                putUsers([matchedUser])
                            } else {
                                // Fallback: fetch a single preview by id (now includes callServices)
                                try {
                                    const userResponse =
                                        await userService.getUserById(
                                            receiverId
                                        )
                                    if (userResponse?.data) {
                                        state.usersMap[receiverId] =
                                            userResponse.data
                                        putUsers([userResponse.data])
                                    }
                                } catch (e) {
                                    console.warn(
                                        'Failed to fetch receiver user by id after sync fallback',
                                        e
                                    )
                                }
                            }
                        }

                        return newConversation._id // Return ID for potential further use
                    } else {
                        throw new Error(
                            'Failed to fetch conversation details after creation.'
                        )
                    }
                } else {
                    console.error(
                        `API failed to create conversation with ${receiverUsername}. Response:`,
                        result
                    )
                    throw new Error(
                        'Conversation creation failed at API level.'
                    )
                }
            } catch (error) {
                console.error(
                    `Error during createConversation action for ${receiverUsername}:`,
                    error
                )
                // Rethrow the error so the component can display feedback
                throw error
            } finally {
                // state.isCreatingConversation = false;
            }
        }

        /**
         * Reloads the current conversation's messages using the latest language preference.
         * Called after language change.
         */
        async function reloadCurrentConversationForLanguage() {
            if (!state.currentConversation) {
                console.warn('Cannot reload messages: No current conversation.')
                return
            }
            const conversationId = state.currentConversation._id
            console.log(
                `Reloading initial messages for ${conversationId} due to language change...`
            )

            state.isLoadingMessages = true
            state.currentMessages = [] // Clear existing messages
            state.hasMoreMessagesToLoad = false

            try {
                // 1. Load the initial page of messages using the existing helper.
                // This helper correctly checks IndexedDB and then falls back to the API.
                const { messages: initialMessages, hasMore } =
                    await loadInitialMessages(conversationId)

                // 2. If translation is enabled, fetch translations for this initial batch.
                if (authStore.user?.data?.translateMessages) {
                    const translatedMessages =
                        await fetchAndMergeTranslations(initialMessages)
                    state.currentMessages = translatedMessages
                } else {
                    state.currentMessages = initialMessages
                }

                state.hasMoreMessagesToLoad = hasMore

                console.log(
                    `Initial message reload complete. Loaded ${state.currentMessages.length} messages. HasMore: ${state.hasMoreMessagesToLoad}`
                )

                // 4. Mark the newly visible messages as read.
                markCurrentConversationRead() // Fire-and-forget
            } catch (error) {
                console.error(
                    `Failed to reload conversation ${conversationId} for language change:`,
                    error
                )
                // Reset state on error to avoid inconsistent UI
                state.currentMessages = []
                state.hasMoreMessagesToLoad = false
            } finally {
                state.isLoadingMessages = false
            }
        }

        async function hasConversationWithUser(targetUserId) {
            if (!authStore.user?.data?._id) {
                return false
            }

            // 1. Check if conversation already exists locally in Pinia state
            const existingInState = state.conversations.find((conv) => {
                return (
                    conv.participantDetails &&
                    conv.participantDetails.some(
                        (p) =>
                            p.userId === targetUserId &&
                            p.userId !== authStore.userId
                    )
                )
            })

            if (existingInState) {
                return true
            }

            // 2. Check IndexedDB
            try {
                const existingInDb = await findConversationByParticipantId(
                    targetUserId,
                    authStore.userId
                )
                return !!existingInDb
            } catch (error) {
                console.error('Error checking conversation in DB:', error)
                return false
            }
        }

        async function findOrCreateConversationWithUser(
            targetUserId,
            targetUsername,
            options = {}
        ) {
            const { skipSetupGate = false } = options

            if (!authStore.user?.data?._id) {
                console.error(
                    'Cannot find/create conversation: Current user not logged in.'
                )
                return false
            }

            // 1. Check if conversation already exists locally in Pinia state
            let existingConversation = state.conversations.find((conv) => {
                // Check if any participant *other than the current user* matches the targetUserId
                return (
                    conv.participantDetails && // Ensure participantDetails exists
                    conv.participantDetails.some(
                        (p) =>
                            p.userId === targetUserId &&
                            p.userId !== authStore.userId
                    )
                )
            })

            if (existingConversation) {
                console.log(
                    `Conversation with ${targetUserId} already exists locally (Pinia). Setting as current.`
                )
                try {
                    await setConversation(existingConversation._id)
                    // Ensure the receiver's user data is present and complete for UI (name, avatar, status, messageServices)
                    try {
                        const receiverId =
                            existingConversation.participantDetails?.find(
                                (p) => p.userId !== authStore.userId
                            )?.userId
                        // Fetch fresh data if missing or incomplete (e.g., missing messageServices for paid messaging detection)
                        const existingUser = state.usersMap[receiverId]
                        const needsFetch = !existingUser || existingUser.messageServices === undefined
                        if (receiverId && needsFetch) {
                            const userResponse =
                                await userService.getUserById(receiverId)
                            if (userResponse?.data) {
                                state.usersMap[receiverId] = userResponse.data
                                putUsers([userResponse.data])
                            }
                        }
                    } catch (ensureErr) {
                        console.warn(
                            'Warning: Failed to ensure receiver user data after setting existing conversation from Pinia.',
                            ensureErr
                        )
                    }
                    return true // Successfully set existing conversation
                } catch (error) {
                    console.error(
                        'Error setting existing conversation from Pinia as current:',
                        error
                    )
                    return false // Failed to set existing conversation
                }
            }

            // 2. If not found in Pinia, check IndexedDB
            console.log(
                `Conversation with ${targetUserId} not found in Pinia. Checking DB...`
            )
            try {
                existingConversation = await findConversationByParticipantId(
                    targetUserId,
                    authStore.userId
                )

                if (existingConversation) {
                    console.log(
                        `Conversation with ${targetUserId} found in DB. Adding to Pinia and setting as current.`
                    )
                    // Add to Pinia state if not already there (shouldn't be, but safety check)
                    // Use unshift to add to the beginning for visibility if needed
                    if (
                        !state.conversations.some(
                            (c) => c._id === existingConversation._id
                        )
                    ) {
                        state.conversations.unshift(existingConversation)
                    }
                    // Set as current
                    await setConversation(existingConversation._id)
                    // Ensure the receiver's user data is present and complete for UI (name, avatar, status, messageServices)
                    try {
                        const receiverId =
                            existingConversation.participantDetails?.find(
                                (p) => p.userId !== authStore.userId
                            )?.userId
                        // Fetch fresh data if missing or incomplete (e.g., missing messageServices for paid messaging detection)
                        const existingUser = state.usersMap[receiverId]
                        const needsFetch = !existingUser || existingUser.messageServices === undefined
                        if (receiverId && needsFetch) {
                            const userResponse =
                                await userService.getUserById(receiverId)
                            if (userResponse?.data) {
                                state.usersMap[receiverId] = userResponse.data
                                putUsers([userResponse.data])
                            }
                        }
                    } catch (ensureErr) {
                        console.warn(
                            'Warning: Failed to ensure receiver user data after setting conversation from DB.',
                            ensureErr
                        )
                    }
                    return true // Successfully found in DB and set
                }
            } catch (dbError) {
                console.error(
                    `Error checking DB for conversation with ${targetUserId}:`,
                    dbError
                )
                // Fall through to API call even if DB check fails
            }

            // 3. If not found locally (Pinia or DB), call the service to find/create on backend
            console.log(
                `Conversation with ${targetUserId} not found locally. Calling API...`
            )
            try {
                // If username is missing, try to fetch it by userId to support API contract
                if (!targetUsername) {
                    try {
                        const userResp =
                            await userService.getUserById(targetUserId)
                        targetUsername = userResp?.data?.username || null
                    } catch (lookupErr) {
                        console.warn(
                            'Could not resolve username from userId before creating conversation:',
                            lookupErr
                        )
                    }
                }

                const conversationResponse =
                    await conversationService.createConversation(targetUsername) // This will throw if backend denies

                if (
                    conversationResponse &&
                    conversationResponse.conversation?._id
                ) {
                    console.log(
                        `Successfully found/created conversation ${conversationResponse.conversation?._id} via API.`
                    )
                    // Ensure the full conversation object is fetched or properly structured
                    const fullConversationDetails =
                        conversationResponse.conversation

                    // Add/Update conversation in state and DB
                    const existingIndex = state.conversations.findIndex(
                        (c) => c._id === fullConversationDetails._id
                    )
                    if (existingIndex > -1) {
                        state.conversations[existingIndex] =
                            fullConversationDetails // Update existing
                    } else {
                        state.conversations.unshift(fullConversationDetails) // Add to beginning
                    }
                    await updateConversation(fullConversationDetails) // Save/Update in IndexedDB

                    // 4. Set as current conversation
                    await setConversation(fullConversationDetails._id)

                    // 5. Join the new conversation room via WebSocket
                    if (authStore.user.data) {
                        // Ensure user data exists before accessing it
                        await conversationSocketService.joinNewConversation(
                            fullConversationDetails._id,
                            authStore.user.data
                        )
                        console.log(
                            `Joined new conversation room ${fullConversationDetails._id}`
                        )
                    } else {
                        console.warn(
                            'User data not available to join new conversation room.'
                        )
                    }

                    // 6. Ensure the receiver's user data is present and complete for UI (name, avatar, status, messageServices)
                    try {
                        const receiverId =
                            fullConversationDetails.participantDetails?.find(
                                (p) => p.userId !== authStore.userId
                            )?.userId
                        // Fetch fresh data if missing or incomplete (e.g., missing messageServices for paid messaging detection)
                        const existingUser = state.usersMap[receiverId]
                        const needsFetch = !existingUser || existingUser.messageServices === undefined
                        if (receiverId && needsFetch) {
                            const userResponse =
                                await userService.getUserById(receiverId)
                            if (userResponse?.data) {
                                state.usersMap[receiverId] = userResponse.data
                                putUsers([userResponse.data])
                            }
                        }
                    } catch (ensureErr) {
                        console.warn(
                            'Warning: Failed to ensure receiver user data after creating conversation via API.',
                            ensureErr
                        )
                    }

                    return true // Successfully created and set
                } else {
                    console.error(
                        'API call for find/create conversation did not return a valid conversation object even on success.'
                    )
                    // Consider this a failure case that should be communicated.
                    // Throwing a generic error or returning false are options.
                    // For consistency with error handling, throwing an error might be better.
                    throw new Error(
                        'Invalid response from server after attempting to create conversation.'
                    )
                }
            } catch (error) {
                console.error(
                    `Error finding or creating conversation with user ${targetUserId} (API call):`,
                    error
                )
                // Re-throw the error so the component layer can handle it specifically
                throw error
            }
        }

        return {
            // State
            state,

            // Getters
            filteredConversations,
            myParticipantDetails,
            otherParticipantDetails,
            currentChatPartner,

            // Conversation Management
            initializeStore,
            setConversation,
            createConversation,
            findOrCreateConversationWithUser,
            hasConversationWithUser,
            loadMoreMessages,
            leaveConversation,

            // Conversation Status
            favoriteConversation,
            unfavoriteConversation,
            approveConversation,
            markCurrentConversationRead,

            // Message Management
            addOptimisticMessage,
            finalizeOptimisticMessage,
            markOptimisticMessageFailed,
            updateOrderInMessage,
            updateMediaObjectRefsInMessages,
            updateMessageTranscripts,

            // Translation & Language
            ensureCurrentMessagesTranslated,
            reloadCurrentConversationForLanguage,

            // UI & Filters
            setMode,

            // Scroll Position Management
            saveScrollPosition: (conversationId, scrollTop) => {
                if (conversationId && typeof scrollTop === 'number') {
                    state.scrollPositions[conversationId] = scrollTop
                }
            },
            getScrollPosition: (conversationId) => {
                return state.scrollPositions[conversationId] ?? null
            },
            clearScrollPosition: (conversationId) => {
                if (conversationId) {
                    delete state.scrollPositions[conversationId]
                }
            },

            // Copilot Suggestions Cache
            getCopilotSuggestions: (conversationId) => {
                if (!conversationId) return []
                return state.copilotSuggestionsByConversation[conversationId] || []
            },
            setCopilotSuggestions: (conversationId, suggestions) => {
                if (!conversationId) return
                state.copilotSuggestionsByConversation[conversationId] =
                    Array.isArray(suggestions) ? suggestions : []
            },
            clearCopilotSuggestions: (conversationId) => {
                if (!conversationId) return
                delete state.copilotSuggestionsByConversation[conversationId]
            },

            // Copilot Audio Ideas Cache
            getCopilotAudioIdeas: (conversationId) => {
                if (!conversationId) return []
                return state.copilotAudioIdeasByConversation[conversationId] || []
            },
            setCopilotAudioIdeas: (conversationId, ideas) => {
                if (!conversationId) return
                state.copilotAudioIdeasByConversation[conversationId] =
                    Array.isArray(ideas) ? ideas : []
            },
            clearCopilotAudioIdeas: (conversationId) => {
                if (!conversationId) return
                delete state.copilotAudioIdeasByConversation[conversationId]
            },

            // Cleanup & Connection
            logoutClean,
        }
    },
    {
        persist: {
            storage: sessionStorage, // or localStorage
            paths: [
                'state.conversationFilters',
                'state.hasMoreMessagesToLoad',
                'state.currentConversation',
                'state.currentMessages',
                'state.usersMap',
                'state.scrollPositions', // Persist scroll positions across navigation
            ], // Only persist things that make sense, like UI state
        },
    }
)
